if (!customElements.get('card-media-gallery')) {
  class CardMediaGallery extends HTMLElement {
    constructor() {
      super();

      this.bindOnButtonClick = this.onButtonClick.bind(this);
      this.bindUpdateGallery = this.updateGallery.bind(this);
      this.bindOnScroll = this.onScroll.bind(this);
      this.bindOnMouseDown = this.onMouseDown.bind(this);
      this.bindOnMouseMove = this.onMouseMove.bind(this);
      this.bindOnMouseLeave = this.onMouseLeave.bind(this);

      this.resizeObserver = new ResizeObserver(entries => {
        requestAnimationFrame(() => {
          this.updateGallery();
        })
      })
    }

    connectedCallback() {
      if (this.closest('.product-card-wrapper')?.dataset.featuredVideo === 'true') {
        return;
      }

      this.observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          requestAnimationFrame(() => {
            this.setUp();
          });
          this.observer.unobserve(this);
        }
      }, { rootMargin: '0px 0px 200px 0px' });

      this.observer.observe(this);
    }

    setUp() {
      this.classList.remove('loaded');
      this.list = this.querySelector('.card-media-gallery__list');
      if(!this.list) return;
      this.items = Array.from(this.querySelectorAll('.card-media-gallery__item, .card-media-gallery__clone'));
      this.clones = Array.from(this.querySelectorAll('.card-media-gallery__clone'));
      this.counter = this.querySelector('.card-media-gallery__counter');
      this.images = Array.from(this.querySelectorAll('.card-media-gallery__image'));
      this.indicators = Array.from(this.querySelectorAll('.card-media-gallery__indicator'));

      this.setPositions();

      this.prevButton = this.querySelector('.card-media-gallery__button.prev');
      this.nextButton = this.querySelector('.card-media-gallery__button.next');

      if (this.prevButton) this.prevButton.addEventListener('click', this.bindOnButtonClick);
      if (this.nextButton) this.nextButton.addEventListener('click', this.bindOnButtonClick);

      this.resizeObserver.observe(this)
      this.list.addEventListener('scroll', this.bindOnScroll);

      this.isDragging = false;
      this.hasMoved = false;
      this.startPos = 0;
      this.startPosY = 0;
      this.list.addEventListener('mousedown', this.bindOnMouseDown, { passive: false });

      this.scrollToFirstItem();

      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const disableAutoplay = this.dataset.disableAutoplay === 'true';

      if (window.innerWidth >= 990 && !this.prefersReducedMotion && !disableAutoplay) {
        this.setupAutoplay();
      }
    }

    disconnectedCallback() {
      this.resizeObserver.disconnect();
      if (this.observer) {
        this.observer.disconnect();
      }
    }

    scrollToFirstItem() {
      let initPosition = 1;
      if (window.innerWidth < 750) {
        initPosition = 0;
      }

      const initItem = this.items.find((item) => +item.dataset.index === initPosition);
      if (initItem) {
        this.list.scrollLeft = +initItem.dataset.position;
      }

      this.classList.add('loaded');
    }

    setPositions() {
      this.positions = [];
      this.items.forEach((item) => {
        const position = Math.round(item.offsetLeft - this.offsetLeft);
        item.dataset.position = position;
        this.positions.push(position);
      });

      this.clones.forEach((clone) => {
        const relItem = this.querySelector(`#${clone.dataset.item}`);
        if (relItem) clone.dataset.relPosition = relItem.dataset.position;
      })
    }

    onButtonClick(event) {
      if (this.autoplayInterval) {
        this.pauseAutoplay();
      }

      const currentScroll = this.list.scrollLeft
      if (event.currentTarget.classList.contains('prev')) {
        const positions = this.positions.filter(p => p < (currentScroll - 10));
        this.list.scroll({
          left: positions[positions.length - 1] || 0,
          behavior: 'smooth'
        })
      } else {
        const positions = this.positions.filter(p => p > (currentScroll + 10));
        this.list.scroll({
          left: positions[0] || this.positions[this.positions.length - 1],
          behavior: 'smooth'
        })
      }

      setTimeout(() => {
        const currentIndex = this.getCurrentSlideIndex();
        this.currentAutoplayIndex = currentIndex;
        this.updateIndicators(currentIndex);
      }, 300);
    }

    onScroll() {
      const currentScroll = Math.round(this.list.scrollLeft)
      const scrollWidth = Math.ceil(this.list.scrollWidth - this.list.getBoundingClientRect().width)
      if (this.prevButton) {
        if(currentScroll <= 5) {
          this.prevButton.setAttribute('disabled', true);
          this.prevButton.setAttribute('aria-disabled', true);
        } else {
          this.prevButton.removeAttribute('disabled');
          this.prevButton.removeAttribute('aria-disabled');
        }
      }

      if(this.nextButton) {
        if(Math.ceil(currentScroll) >= (scrollWidth) - 5) {
          this.nextButton.setAttribute('disabled', true);
          this.nextButton.setAttribute('aria-disabled', true);
        } else {
          this.nextButton.removeAttribute('disabled');
          this.nextButton.removeAttribute('aria-disabled');
        }
      }

      if (this.counter) {
        let percentage = Math.round((currentScroll / ((scrollWidth) - this.items[0].getBoundingClientRect().width)) * 100);
        if (percentage > 100) {
          percentage -= 100;
        }
        this.counter.style.setProperty('--percentage', `${percentage}%`)
      }

      if (!this.autoplayInterval && this.indicators) {
        const currentIndex = this.getCurrentSlideIndex();
        this.updateIndicators(currentIndex);
      }
    }

    onMouseDown(event) {
      if (this.autoplayInterval) {
        this.pauseAutoplay();
      }

      this.isDragging = true;
      this.hasMoved = false;
      this.diff = 0;
      this.list.classList.remove('touch');

      this.startPos = event.pageX - this.list.offsetLeft;
      this.initScrollLeft = this.list.scrollLeft;
      this.list.classList.add('grabbing');

      this.list.addEventListener('mousemove', this.bindOnMouseMove, { passive: false });
      this.list.addEventListener('mouseup', this.bindOnMouseLeave, { passive: false });
      this.list.addEventListener('mouseleave', this.bindOnMouseLeave, { passive: false });
      window.addEventListener('mouseleave', this.bindOnMouseLeave, { passive: false });
      window.addEventListener('mouseup', this.bindOnMouseLeave, { passive: false });
    }

    onMouseMove(event) {
      if (!this.isDragging) return;
      const x = event.pageX - this.list.offsetLeft;
      this.walk = x - this.startPos;

      this.list.classList.add('moving');
      this.hasMoved = true;

      if(Math.abs(this.walk) < 10) return;
      event.preventDefault();

      this.list.scrollLeft = this.initScrollLeft -  this.walk;
    }

    onMouseLeave() {
      this.isDragging = false;
      this.list.classList.remove('grabbing');

      const currentScroll = Math.round(this.list.scrollLeft);
      let targetPosition = null;
      let needsInfiniteSnap = false;

      if (this.walk > 10) {
        const positions = this.positions.filter(p => p < (currentScroll - 10));
        targetPosition = positions[positions.length - 1];

        if (targetPosition === undefined && this.positions.length > 0) {
          targetPosition = this.positions[0];
        }
      } else if (this.walk < -10) {
        const positions = this.positions.filter(p => p > (currentScroll + 10));
        targetPosition = positions[0];

        if (targetPosition === undefined && this.positions.length > 0) {
          targetPosition = this.positions[this.positions.length - 1];
        }
      }

      if (targetPosition !== null && targetPosition !== undefined) {
        this.list.scroll({
          left: targetPosition,
          behavior: 'smooth'
        });

        const targetItem = this.items.find(item => +item.dataset.position === targetPosition);
        if (targetItem && targetItem.classList.contains('card-media-gallery__clone')) {
          needsInfiniteSnap = true;
        }
      }

      setTimeout(() => {
        this.list.classList.remove('moving');

        if (needsInfiniteSnap) {
          const currentIndex = this.getCurrentSlideIndex();
          const realItem = this.items.find(item =>
            !item.classList.contains('card-media-gallery__clone') &&
            +item.dataset.index === currentIndex
          );

          if (realItem) {
            setTimeout(() => {
              this.list.scroll({
                left: +realItem.dataset.position,
                behavior: 'instant'
              });

              this.currentAutoplayIndex = currentIndex;
              this.updateIndicators(currentIndex);

              if (this.autoplayPaused) {
                setTimeout(() => {
                  if (this.indicators && this.indicators[this.currentAutoplayIndex] && this.autoplayInterval) {
                    this.indicators[this.currentAutoplayIndex].classList.add('card-media-gallery__indicator--filling');
                  }
                }, 50);
              }
            }, 300);
          }
        } else {
          const currentIndex = this.getCurrentSlideIndex();
          this.currentAutoplayIndex = currentIndex;
          this.updateIndicators(currentIndex);

          if (this.autoplayPaused) {
            setTimeout(() => {
              if (this.indicators && this.indicators[this.currentAutoplayIndex] && this.autoplayInterval) {
                this.indicators[this.currentAutoplayIndex].classList.add('card-media-gallery__indicator--filling');
              }
            }, 50);
          }
        }
      }, 300);

      this.list.removeEventListener('mousemove', this.bindOnMouseMove, { passive: false });
      this.list.removeEventListener('mouseup', this.bindOnMouseLeave, { passive: false });
      this.list.removeEventListener('mouseleave', this.bindOnMouseLeave, { passive: false });
      window.removeEventListener('mouseleave', this.bindOnMouseLeave, { passive: false });
      window.removeEventListener('mouseup', this.bindOnMouseLeave, { passive: false });
    }

    updateGallery() {
      this.setPositions();
      const width = this.getBoundingClientRect().width;
      const mainImage = this.closest('.media').querySelector('.card__media__featured');
      if (mainImage) mainImage.sizes = `${width}px`;
      this.images.forEach(image => {
        image.sizes = `${width}px`;
      })
    }

    setupAutoplay() {
      this.hoverTimeout = null;
      this.autoplayInterval = null;
      this.autoplayPaused = false;
      this.resumeTimeout = null;
      this.currentAutoplayIndex = 0;

      const card = this.closest('.card');
      if (!card) return;

      card.addEventListener('mouseenter', () => {
        this.startAutoplay();
      });

      card.addEventListener('mouseleave', () => {
        clearTimeout(this.hoverTimeout);
        this.stopAutoplay();
        this.resetToFirst();
      });
    }

    startAutoplay() {
      if (this.autoplayInterval || this.autoplayPaused) {
        return;
      }

      const realItems = this.items.filter(item => !item.classList.contains('card-media-gallery__clone'));
      if (realItems.length <= 1) return;

      this.currentAutoplayIndex = 1;
      this.goToSlide(this.currentAutoplayIndex);
      this.updateIndicators(this.currentAutoplayIndex);

      setTimeout(() => {
        if (this.indicators && this.indicators[this.currentAutoplayIndex]) {
          this.indicators[this.currentAutoplayIndex].classList.add('card-media-gallery__indicator--filling');
        }
      }, 50);

      this.autoplayInterval = setInterval(() => {
        if (!this.isHandlingLoop) {
          this.currentAutoplayIndex++;

          if (this.currentAutoplayIndex >= realItems.length) {
            this.handleInfiniteLoop(realItems.length);
          } else {
            this.goToSlide(this.currentAutoplayIndex);
            this.updateIndicators(this.currentAutoplayIndex);
          }
        } else {
          this.updateIndicators(this.currentAutoplayIndex);
        }
      }, 4000);
    }

    handleInfiniteLoop(totalItems) {
      if (this.isHandlingLoop) {
        return;
      }

      this.isHandlingLoop = true;
      this.goToSlide(totalItems);

      this.waitForScrollEnd(() => {
        this.setPositions();

        const realFirstItem = this.items.find(item =>
          !item.classList.contains('card-media-gallery__clone') &&
          +item.dataset.index === 0
        );

        if (realFirstItem) {
          this.list.scrollLeft = Math.round(+realFirstItem.dataset.position);
        }

        this.currentAutoplayIndex = 0;

        this.updateIndicators(0, true);

        setTimeout(() => {
          if (this.indicators && this.indicators[0]) {
            this.indicators[0].classList.add('card-media-gallery__indicator--filling');
          }
        }, 50);

        this.isHandlingLoop = false;
      });
    }

    waitForScrollEnd(callback) {
      if (this.scrollEndTimeout) {
        clearTimeout(this.scrollEndTimeout);
      }

      const checkScrollEnd = () => {
        const currentScroll = Math.round(this.list.scrollLeft);

        this.scrollEndTimeout = setTimeout(() => {
          const newScroll = Math.round(this.list.scrollLeft);
          if (Math.abs(newScroll - currentScroll) < 1) {
            callback();
          } else {
            checkScrollEnd();
          }
        }, 100);
      };

      checkScrollEnd();
    }

    stopAutoplay() {
      clearInterval(this.autoplayInterval);
      clearTimeout(this.resumeTimeout);
      clearTimeout(this.scrollEndTimeout);
      this.autoplayInterval = null;
      this.autoplayPaused = false;
      this.currentAutoplayIndex = 0;
      this.isHandlingLoop = false;
    }

    pauseAutoplay() {
      this.stopAutoplay();
      this.autoplayPaused = true;

      const currentIndicator = this.indicators[this.currentAutoplayIndex];
      if (currentIndicator) {
        currentIndicator.classList.remove('card-media-gallery__indicator--filling');
      }

      clearTimeout(this.resumeTimeout);
      this.resumeTimeout = setTimeout(() => {
        this.autoplayPaused = false;
        const card = this.closest('.card');
        if (card && card.matches(':hover')) {
          this.resumeAutoplay();
        }
      }, 2000);
    }

    resumeAutoplay() {
      if (this.autoplayInterval || this.autoplayPaused) {
        return;
      }

      const realItems = this.items.filter(item => !item.classList.contains('card-media-gallery__clone'));
      if (realItems.length <= 1) return;

      const currentIndex = this.getCurrentSlideIndex();
      this.currentAutoplayIndex = currentIndex;

      this.updateIndicators(this.currentAutoplayIndex);

      setTimeout(() => {
        if (this.indicators && this.indicators[this.currentAutoplayIndex]) {
          this.indicators[this.currentAutoplayIndex].classList.add('card-media-gallery__indicator--filling');
        }
      }, 50);

      this.autoplayInterval = setInterval(() => {
        if (!this.isHandlingLoop) {
          this.currentAutoplayIndex++;

          if (this.currentAutoplayIndex >= realItems.length) {
            this.handleInfiniteLoop(realItems.length);
          } else {
            this.goToSlide(this.currentAutoplayIndex);
            this.updateIndicators(this.currentAutoplayIndex);
          }
        } else {
          this.updateIndicators(this.currentAutoplayIndex);
        }
      }, 4000);
    }

    goToSlide(index, smooth = true) {
      const targetItem = this.items.find(item => +item.dataset.index === index);

      if (targetItem) {
        this.list.scroll({
          left: +targetItem.dataset.position,
          behavior: smooth ? 'smooth' : 'instant'
        });
      }
    }

    resetToFirst() {
      const initPosition = window.innerWidth < 750 ? 0 : 1;
      const initItem = this.items.find((item) => +item.dataset.index === initPosition);

      if (initItem) {
        this.list.scroll({
          left: +initItem.dataset.position,
          behavior: 'smooth'
        });
      }

      this.updateIndicators(0);
    }

    updateIndicators(activeIndex, skipAnimation = false) {
      if (!this.indicators || this.indicators.length === 0) return;

      this.indicators.forEach((indicator, index) => {
        indicator.classList.remove(
          'card-media-gallery__indicator--active',
          'card-media-gallery__indicator--filling',
          'card-media-gallery__indicator--completed'
        );

        if (index < activeIndex) {
          indicator.classList.add('card-media-gallery__indicator--completed');
        } else if (index === activeIndex) {
          indicator.classList.add('card-media-gallery__indicator--active');

          if (!skipAnimation && window.innerWidth >= 990 && !this.prefersReducedMotion && this.autoplayInterval !== null) {
            void indicator.offsetWidth;

            requestAnimationFrame(() => {
              indicator.classList.add('card-media-gallery__indicator--filling');
            });
          }
        }
      });
    }

    getCurrentSlideIndex() {
      const currentScroll = Math.round(this.list.scrollLeft);
      const realItems = this.items.filter(item => !item.classList.contains('card-media-gallery__clone'));

      let closestIndex = 0;
      let minDistance = Infinity;
      let closestItem = null;

      this.items.forEach((item) => {
        const itemPosition = +item.dataset.position;
        const distance = Math.abs(currentScroll - itemPosition);

        if (distance < minDistance) {
          minDistance = distance;
          closestItem = item;
        }
      });

      if (closestItem && closestItem.classList.contains('card-media-gallery__clone')) {
        const cloneIndex = +closestItem.dataset.index;
        if (cloneIndex === -1) {
          closestIndex = realItems.length - 1;
        } else if (cloneIndex >= realItems.length) {
          closestIndex = 0;
        }
      } else {
        realItems.forEach((item, index) => {
          if (item === closestItem) {
            closestIndex = index;
          }
        });
      }

      return closestIndex;
    }
  }

  customElements.define('card-media-gallery', CardMediaGallery)
}